import { useCallback, useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '@/context/auth.context';
import { useViewMode } from '@/context/view-mode.context';
import { useToast } from '@/context/ToastContext';
import { isAdmin } from '@/utils/user';
import { useAgentPanel } from '@/context/agent-panel.context';
import { useAgentState } from '@/context/agent-state.context';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { HeadCircuit } from '@phosphor-icons/react';
import Page from 'src/components/page/Page';
import {
  ForegroundAgentChat,
  type MemoryBankNotePayload,
} from '@/components/common/ForegroundAgentChat';
import { useCreateMemoryBankNote } from '@/api/core/ai/memory-bank/mutations';
import { orpc } from '@/api/rpc/client';
import type { MemoryBankSupportedEntityTypes } from '@erp/rpc';
import useSalesOrderDraft from 'src/api/accounts-receivable/sales-order-drafts/useSalesOrderDraft';
import useSalesOrderDraftsV2 from 'src/api/accounts-receivable/sales-order-drafts/useSalesOrderDraftsV2';
import useApproveSalesOrderDraft from 'src/api/accounts-receivable/sales-order-drafts/useApproveSalesOrderDraft';
import useRejectSalesOrderDraft from 'src/api/accounts-receivable/sales-order-drafts/useRejectSalesOrderDraft';
import useDeleteSalesOrderDraft from 'src/api/accounts-receivable/sales-order-drafts/useDeleteSalesOrderDraft';
import { SalesOrderDraftStatus } from 'src/types/application/SalesOrderDraftDTO';
import { SalesOrderDraftStatusBadge } from 'src/components/sales-order-draft-status-badge/SalesOrderDraftStatusBadge';
import { SalesOrderSyncStatus } from '@/types/application/SalesOrderDTOV2_DEPRECATED';
import SalesOrderDraftView from './components/SalesOrderDraftView';
import Watchers from 'src/components/watchers/Watchers';

const SalesOrderDraftPage: React.FC = () => {
  const { draftId } = useParams({
    from: '/authenticated/sales-orders/draft/$draftId',
  });
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const agentPanel = useAgentPanel();
  const { reset: resetAgentChat } = useAgentState();
  const queryClient = useQueryClient();
  const createMemoryBankNote = useCreateMemoryBankNote();
  const { showToast } = useToast();

  // Reset and close the AI agent chat when entering/leaving this page
  useEffect(() => {
    resetAgentChat();
    agentPanel.close();

    return () => {
      resetAgentChat();
      agentPanel.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run on mount/unmount, not when agentPanel changes
  }, [resetAgentChat]);

  // Fetch current draft
  const {
    data: salesOrderDraftData,
    isLoading,
    refetch: refetchSalesOrderDraft,
  } = useSalesOrderDraft(draftId);

  const salesOrderDraft = salesOrderDraftData?.stagedPurchaseOrder;

  // Fetch list of drafts for navigation
  const { data: draftsListData } = useSalesOrderDraftsV2({
    pageSize: 100, // Fetch enough for basic navigation
  });

  // Find current position in the list
  const currentIndex =
    draftsListData?.edges?.findIndex((d) => d.id === draftId) ?? -1;
  const prevDraftId =
    currentIndex > 0 ? draftsListData?.edges?.[currentIndex - 1]?.id : null;
  const nextDraftId =
    currentIndex >= 0 && currentIndex < (draftsListData?.edges?.length ?? 0) - 1
      ? draftsListData?.edges?.[currentIndex + 1]?.id
      : null;

  // Track if AI suggestion indicates ready for approval
  const [isReadyForApproval, setIsReadyForApproval] = useState(false);

  // Track if inventory column should be shown (enabled via chat widget)
  const [showInventoryColumn, setShowInventoryColumn] = useState(() => {
    return (
      localStorage.getItem('salesOrderDraft_showInventoryColumn') === 'true'
    );
  });

  // Persist inventory column preference to localStorage
  useEffect(() => {
    localStorage.setItem(
      'salesOrderDraft_showInventoryColumn',
      showInventoryColumn.toString(),
    );
  }, [showInventoryColumn]);

  // Check if sales order draft is already processed (approved or rejected)
  const isProcessed =
    salesOrderDraft?.status === SalesOrderDraftStatus.APPROVED ||
    salesOrderDraft?.status === SalesOrderDraftStatus.REJECTED;

  // Determine if actions should be shown
  const showActions = !isProcessed;

  // Approve mutation
  const approveStagedPurchaseOrder = useApproveSalesOrderDraft(
    draftId,
    ({ salesOrderId, originSyncStatus }) => {
      showToast({
        title: 'Sales Order Created',
        message: 'The sales order has been approved and created successfully.',
        color: 'success',
      });
      navigate({ to: '/sales-orders' });
    },
  );

  // Reject mutation
  const rejectStagedPurchaseOrder = useRejectSalesOrderDraft(draftId, () => {
    navigate({ to: '/sales-orders' });
  });

  // Delete mutation
  const deleteStagedPurchaseOrder = useDeleteSalesOrderDraft(draftId, () => {
    navigate({ to: '/sales-orders' });
  });

  const handleBack = () => {
    navigate({ to: '/sales-orders' });
  };

  const handleNavigatePrev = useCallback(() => {
    if (prevDraftId) {
      navigate({
        to: '/sales-orders/draft/$draftId',
        params: { draftId: prevDraftId },
      });
    }
  }, [prevDraftId, navigate]);

  const handleNavigateNext = useCallback(() => {
    if (nextDraftId) {
      navigate({
        to: '/sales-orders/draft/$draftId',
        params: { draftId: nextDraftId },
      });
    }
  }, [nextDraftId, navigate]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't navigate if user is typing in an input
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      if (event.key === 'ArrowLeft' && prevDraftId) {
        event.preventDefault();
        handleNavigatePrev();
      } else if (event.key === 'ArrowRight' && nextDraftId) {
        event.preventDefault();
        handleNavigateNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [prevDraftId, nextDraftId, handleNavigatePrev, handleNavigateNext]);

  const handleToggleAI = () => {
    agentPanel.toggle();
  };

  const handleApprove = () => {
    approveStagedPurchaseOrder.mutate();
  };

  const handleReject = () => {
    rejectStagedPurchaseOrder.mutate();
  };

  const handleDelete = () => {
    if (
      window.confirm(
        'Are you sure you want to permanently delete this sales order draft? This action cannot be undone.',
      )
    ) {
      deleteStagedPurchaseOrder.mutate();
    }
  };

  const handleUpdate = () => {
    refetchSalesOrderDraft();
  };

  const handleApproved = (salesOrderId: string) => {
    navigate({
      to: '/sales-orders/$salesOrderId',
      params: { salesOrderId },
    });
  };

  const handleRejected = () => {
    navigate({ to: '/sales-orders' });
  };

  // Handle field creation from chat widget
  const handleCreateField = useCallback((field: { id: string }) => {
    if (field.id === 'line-item-inventory') {
      setShowInventoryColumn(true);
    }
  }, []);

  // Handle field removal from chat widget
  const handleRemoveField = useCallback((field: { id: string }) => {
    if (field.id === 'line-item-inventory') {
      setShowInventoryColumn(false);
      localStorage.removeItem('salesOrderDraft_showInventoryColumn');
    }
  }, []);

  // Handle memory bank note creation from chat widget
  const handleCreateMemoryBankNote = useCallback(
    async (note: MemoryBankNotePayload) => {
      await createMemoryBankNote.mutateAsync({
        note: note.content,
        entityId: note.entityId,
        entityType: note.entityType as MemoryBankSupportedEntityTypes,
        metadata: note.metadata as Record<string, string | undefined>,
      });

      // Invalidate queries so MemoryBankNotes icon updates
      queryClient.invalidateQueries({
        queryKey: orpc.memoryBank.listUsages.key({
          input: {
            entityId: note.entityId,
            entityType: note.entityType as MemoryBankSupportedEntityTypes,
          },
        }),
      });
    },
    [createMemoryBankNote, queryClient],
  );

  const isMutating =
    approveStagedPurchaseOrder.isLoading ||
    rejectStagedPurchaseOrder.isLoading ||
    deleteStagedPurchaseOrder.isLoading;

  if (isLoading) {
    return (
      <Page>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Page>
    );
  }

  if (!salesOrderDraft) {
    return (
      <Page>
        <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
          <p className="text-muted-foreground">Sales order draft not found</p>
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Sales Orders
          </Button>
        </div>
      </Page>
    );
  }

  return (
    <Page>
      {/* AI Button - Fixed at top right */}
      <Button
        size="icon"
        onClick={handleToggleAI}
        className="fixed top-2 right-2 z-50 h-9 w-9 rounded-lg bg-stone-700 hover:bg-stone-800 text-white shadow-lg"
        aria-label="Open AI Assistant"
      >
        <HeadCircuit size={22} weight="duotone" />
      </Button>

      <div className="flex flex-col h-full gap-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold">
                {salesOrderDraft.status === SalesOrderDraftStatus.APPROVED
                  ? 'Sales Order'
                  : 'Sales Order Draft'}
              </h1>
              <Watchers
                watchableEntityType="staged_purchase_order"
                watchableId={draftId}
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Navigation */}
            {currentIndex >= 0 && (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleNavigatePrev}
                  disabled={!prevDraftId}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground min-w-[60px] text-center">
                  {currentIndex + 1} of {draftsListData?.pageInfo?.totalCount}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleNavigateNext}
                  disabled={!nextDraftId}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Status Badge */}
            {salesOrderDraft.status && (
              <SalesOrderDraftStatusBadge
                status={salesOrderDraft.status}
                isReadyForApproval={isReadyForApproval}
              />
            )}
          </div>
        </div>

        {/* Content - use calc height to account for header (~60px), page padding (24px*2), header row (~40px), actions (~60px), gaps (16px*3) */}
        <Card
          className="overflow-hidden"
          style={{ height: 'calc(100vh - 250px)' }}
        >
          <CardContent className="h-full p-4 overflow-hidden">
            <SalesOrderDraftView
              salesOrderDraftId={draftId}
              editable={!isProcessed}
              onUpdate={handleUpdate}
              onApproved={handleApproved}
              onRejected={handleRejected}
              onReadyForApprovalChange={setIsReadyForApproval}
              onApproveOrder={handleApprove}
              showInventoryColumn={showInventoryColumn}
            />
          </CardContent>
        </Card>

        {/* Actions */}
        {showActions && (
          <div className="flex items-center justify-between border-t pt-4">
            <div className="flex items-center gap-2">
              {isAdmin(currentUser, viewMode) && (
                <Button
                  variant="ghost"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={handleDelete}
                  disabled={isMutating}
                >
                  Delete
                </Button>
              )}
              <Button
                variant="ghost"
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={handleReject}
                disabled={isMutating}
              >
                Ignore
              </Button>
            </div>

            <Button
              onClick={handleApprove}
              disabled={isMutating || !isReadyForApproval}
            >
              {approveStagedPurchaseOrder.isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Approving...
                </>
              ) : (
                'Approve and Create Sales Order'
              )}
            </Button>
          </div>
        )}
      </div>

      <ForegroundAgentChat
        moduleKey="sales-orders"
        currentPage={`/sales-orders/draft/${draftId}`}
        hideFloatingButton
        onCreateField={handleCreateField}
        onRemoveField={handleRemoveField}
        onCreateMemoryBankNote={handleCreateMemoryBankNote}
      />
    </Page>
  );
};

export default SalesOrderDraftPage;
